/* Native review rendering engine.
 * Reads assets/reviews.json (empty by default — see REVIEW_IMPORT_INSTRUCTIONS.md).
 * Never fabricates ratings, counts, or testimonials: every number here is computed
 * from whatever is actually present in reviews.json, and the whole UI stays hidden
 * until real data exists.
 *
 * Expected review shape (see REVIEW_MIGRATION.md):
 * { id, author, rating, date, country, bodyOriginal, bodyTranslated, originalLanguage,
 *   variant, images: [], source, verified }
 */
(function () {
  'use strict';

  function initReviewsApp(root) {
    if (root.dataset.reviewsInit) return;
    root.dataset.reviewsInit = 'true';

    var url = root.getAttribute('data-reviews-url');
    var isMobile = window.matchMedia('(max-width: 767px)').matches;
    var pageSize = isMobile
      ? parseInt(root.getAttribute('data-mobile-initial'), 10) || 4
      : parseInt(root.getAttribute('data-desktop-initial'), 10) || 6;

    var state = { reviews: [], filtered: [], visible: pageSize, filter: 'all' };

    var elEmpty = root.querySelector('[data-reviews-empty]');
    var elGrid = root.querySelector('[data-reviews-grid]');
    var elStats = root.querySelector('[data-reviews-stats]');
    var elDistribution = root.querySelector('[data-reviews-distribution]');
    var elControls = root.querySelector('[data-reviews-controls]');
    var elFooter = root.querySelector('[data-reviews-footer]');
    var template = document.getElementById('review-card-template');

    fetch(url).then(function (r) { return r.ok ? r.json() : []; }).catch(function () { return []; }).then(function (data) {
      state.reviews = Array.isArray(data) ? data : [];
      if (!state.reviews.length) return; // graceful empty state stays visible, nothing else to do
      render();
      injectStructuredData();
      updateHeroSummaries();
    });

    function average() {
      var sum = state.reviews.reduce(function (a, r) { return a + (r.rating || 0); }, 0);
      return sum / state.reviews.length;
    }

    function renderStats() {
      elEmpty.hidden = true;
      elStats.hidden = false;
      elDistribution.hidden = false;
      elControls.hidden = false;

      var avg = average();
      elStats.querySelector('[data-reviews-average]').textContent = avg.toFixed(1);
      elStats.querySelector('[data-reviews-count]').textContent = (window.themeStrings && window.themeStrings.basedOn) || ('Based on ' + state.reviews.length + ' reviews');
      var starsWrap = elStats.querySelector('[data-reviews-stars]');
      starsWrap.innerHTML = starsMarkup(avg);

      for (var i = 1; i <= 5; i++) {
        var count = state.reviews.filter(function (r) { return Math.round(r.rating) === i; }).length;
        var pct = (count / state.reviews.length) * 100;
        var row = elDistribution.querySelector('[data-distribution-row="' + i + '"]');
        if (row) {
          row.querySelector('[data-distribution-fill]').style.width = pct + '%';
          row.querySelector('[data-distribution-count]').textContent = count;
        }
      }
    }

    function starsMarkup(rating) {
      var full = Math.round(rating);
      var html = '';
      for (var i = 0; i < 5; i++) {
        html += '<svg viewBox="0 0 20 20" ' + (i < full ? 'fill="currentColor"' : 'fill="none" stroke="currentColor" stroke-width="1.3"') + '><path d="M10 1.5l2.6 5.6 6 .7-4.5 4.1 1.2 6-5.3-3-5.3 3 1.2-6L1.4 7.8l6-.7z"/></svg>';
      }
      return html;
    }

    function applyFilter() {
      state.filtered = state.reviews.filter(function (r) {
        if (state.filter === 'all') return true;
        if (state.filter === 'photos') return r.images && r.images.length > 0;
        return Math.round(r.rating) === parseInt(state.filter, 10);
      });
      state.visible = pageSize;
      renderGrid();
    }

    function renderGrid() {
      elGrid.innerHTML = '';
      elGrid.hidden = state.filtered.length === 0;
      var slice = state.filtered.slice(0, state.visible);
      slice.forEach(function (review) { elGrid.appendChild(buildCard(review)); });
      elFooter.hidden = state.visible >= state.filtered.length;
    }

    function buildCard(review) {
      var node = template.content.cloneNode(true);
      var article = node.querySelector('[data-review-card]');
      article.querySelector('[data-card-stars]').innerHTML = starsMarkup(review.rating || 0);

      if (review.verified) {
        var v = article.querySelector('[data-card-verified]');
        v.hidden = false;
        v.textContent = 'Verified Buyer';
      }

      var bodyText = review.bodyTranslated || review.bodyOriginal || '';
      article.querySelector('[data-card-body]').textContent = bodyText;

      if (review.bodyTranslated && review.originalLanguage && review.originalLanguage !== 'en') {
        var t = article.querySelector('[data-card-translated]');
        t.hidden = false;
        t.textContent = 'Translated from ' + review.originalLanguage;
      }

      if (review.images && review.images.length) {
        var imagesWrap = article.querySelector('[data-card-images]');
        imagesWrap.hidden = false;
        review.images.forEach(function (src, idx) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'review-card__image';
          btn.setAttribute('data-review-image', '');
          btn.setAttribute('data-full-src', src);
          btn.innerHTML = '<img src="' + src + '" loading="lazy" alt="Customer photo">';
          btn.addEventListener('click', function () { openLightbox(review, idx); });
          imagesWrap.appendChild(btn);
        });
      }

      article.querySelector('[data-card-author]').textContent = review.author || '';
      article.querySelector('[data-card-country]').textContent = review.country || '';
      article.querySelector('[data-card-date]').textContent = review.date || '';
      if (review.variant) article.querySelector('[data-card-variant]').textContent = review.variant;

      return node;
    }

    function render() {
      renderStats();
      applyFilter();
    }

    elControls && elControls.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-review-filter]');
      if (!btn) return;
      root.querySelectorAll('[data-review-filter]').forEach(function (b) { b.classList.remove('is-active'); });
      btn.classList.add('is-active');
      state.filter = btn.getAttribute('data-review-filter');
      applyFilter();
      window.trackEvent('review_filter_clicked', { filter: state.filter });
    });

    elFooter && elFooter.addEventListener('click', function (e) {
      if (!e.target.closest('[data-reviews-show-more]')) return;
      state.visible += pageSize;
      renderGrid();
    });

    // Lightbox
    var lightbox = document.querySelector('[data-review-lightbox]');
    var lightboxContext = { review: null, index: 0 };

    function openLightbox(review, index) {
      lightboxContext = { review: review, index: index };
      updateLightbox();
      lightbox.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      window.trackEvent('review_image_opened');
    }
    function closeLightbox() {
      lightbox.hidden = true;
      document.documentElement.style.overflow = '';
    }
    function updateLightbox() {
      var review = lightboxContext.review;
      if (!review) return;
      lightbox.querySelector('[data-lightbox-image]').src = review.images[lightboxContext.index];
      lightbox.querySelector('[data-lightbox-stars]').innerHTML = starsMarkup(review.rating || 0);
      lightbox.querySelector('[data-lightbox-body]').textContent = review.bodyTranslated || review.bodyOriginal || '';
      lightbox.querySelector('[data-lightbox-author]').textContent = review.author || '';
      lightbox.querySelector('[data-lightbox-date]').textContent = review.date || '';
    }
    if (lightbox) {
      lightbox.querySelectorAll('[data-lightbox-close]').forEach(function (el) { el.addEventListener('click', closeLightbox); });
      lightbox.querySelector('[data-lightbox-prev]').addEventListener('click', function () {
        var imgs = lightboxContext.review.images;
        lightboxContext.index = (lightboxContext.index - 1 + imgs.length) % imgs.length;
        updateLightbox();
      });
      lightbox.querySelector('[data-lightbox-next]').addEventListener('click', function () {
        var imgs = lightboxContext.review.images;
        lightboxContext.index = (lightboxContext.index + 1) % imgs.length;
        updateLightbox();
      });
      document.addEventListener('keydown', function (e) {
        if (lightbox.hidden) return;
        if (e.key === 'Escape') closeLightbox();
        if (e.key === 'ArrowLeft') lightbox.querySelector('[data-lightbox-prev]').click();
        if (e.key === 'ArrowRight') lightbox.querySelector('[data-lightbox-next]').click();
      });
    }

    function updateHeroSummaries() {
      var avg = average();
      var text = avg.toFixed(1) + '/5 · ' + state.reviews.length + ' Reviews';
      document.querySelectorAll('[data-reviews-summary]').forEach(function (el) {
        el.hidden = false;
        var starsEl = el.querySelector('.stars');
        if (starsEl) starsEl.innerHTML = starsMarkup(avg);
        var textEl = el.querySelector('[data-reviews-summary-text]');
        if (textEl) textEl.textContent = text;
      });
    }

    function injectStructuredData() {
      if (!state.reviews.length) return;
      var avg = average();
      var script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: root.getAttribute('data-product-title'),
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: avg.toFixed(1),
          reviewCount: state.reviews.length
        }
      });
      document.head.appendChild(script);
    }
  }

  function init(root) {
    (root || document).querySelectorAll('[data-reviews-app]').forEach(initReviewsApp);
  }

  document.addEventListener('DOMContentLoaded', function () { init(document); });
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
